import React, { useState, useMemo } from "react";

import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import prod1 from "../assets/images/imageProd.png";
import prod2 from "../assets/images/imageProd2.png";
import prod3 from "../assets/images/imageProd3.png";
import { Skeleton } from "./ui/skeleton";
import { useInvestProducts } from "../hooks/query/queries";
import CurrencyFormatter from "../utils/currencyFormatter";
import StringFormatter from "../utils/stringFormatter";
import { Flame, Star } from "lucide-react";
export default function InvestProducts({ searchTerm, onView }) {
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const {
    data: investProducts,
    isLoading: loading,
    error,
  } = useInvestProducts();

  console.log("investProducts", investProducts);
  const string = new StringFormatter();
  const invProdArray = investProducts?.results;
  const filteredResults = useMemo(() => {
    if (!invProdArray) return [];

    return invProdArray.filter((item) => {
      const matchesSearch = item?.name
        ?.toLowerCase()
        .includes(searchTerm?.toLowerCase());

      const matchesFilter =
        selectedFilter === "All" ||
        item?.investmentType?.toLowerCase() === selectedFilter?.toLowerCase();
      const result = matchesSearch && matchesFilter;
      return result;
    });
  }, [invProdArray, searchTerm, selectedFilter]);

  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredResults.slice(startIndex, endIndex);
  }, [filteredResults, currentPage, itemsPerPage]);
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const filterOptions = ["All", "Savings", "Commodities", "Property", "Other"];
  const formatter = new CurrencyFormatter();
  return (
    <section>
      <div className="flex justify-between items-start my-4">
        <p className="text-gray-500">All Products</p>
        <ToggleGroup
          type="single"
          value={selectedFilter}
          onValueChange={(option) => setSelectedFilter(option)}
          className="flex gap-2 my-4"
        >
          {filterOptions.map((option) => (
            <ToggleGroupItem
              key={option}
              value={option}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedFilter === option
                  ? "hover:bg-brand-primary/80 hover:text-white data-[state=on]:bg-brand-primary data-[state=on]:text-white"
                  : "bg-transparent hover:bg-gray-300 text-gray-600"
              }`}
            >
              {option}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
        {loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="max-w-l rounded-2xl overflow-hidden shadow-lg bg-white flex flex-col"
            >
              <div className="h-48 w-full">
                <Skeleton className="w-full h-full bg-gray-300" />
              </div>

              <div className="flex flex-col justify-between flex-1 p-4">
                <div className="mb-4 space-y-2">
                  <Skeleton className="h-4 w-3/4 bg-gray-300" />
                  <Skeleton className="h-3 w-1/2 bg-gray-300" />
                </div>

                <div className="grid grid-cols-3 gap-y-2 mb-3">
                  <Skeleton className="h-3 w-4/5 col-span-2 bg-gray-300" />
                  <Skeleton className="h-3 w-3/4 bg-gray-300" />
                  <Skeleton className="h-3 w-4/5 col-span-2 bg-gray-300" />
                  <Skeleton className="h-3 w-3/4 bg-gray-300" />
                </div>

                <Skeleton className="h-10 w-full rounded-full bg-gray-300" />
              </div>
            </div>
          ))
        ) : invProdArray?.length > 0 ? (
          // Show product cards
          paginatedResults?.map((product, index) => (
            <div
              key={index}
              className="max-w-l rounded-2xl overflow-hidden shadow-lg bg-white flex flex-col"
            >
              <div className="h-48 w-full overflow-hidden relative">
                {/* Badge */}
                {product?.state !== "other" && (
                  <div className="absolute top-4 right-4 text-xs px-4 py-1 rounded-full font-bold bg-[#F1EDF7] text-yellow-800 shadow">
                    <div className="flex items-center justify-between gap-2 text-[#57249C]">
                      {product?.state === "recommended" && (
                        <Star className="w-3 h-3" />
                      )}
                      {product?.state === "popular" && (
                        <Flame className="w-3.5 h-3.5" />
                      )}
                      {string.capitalizeFirst(product?.state)}
                    </div>
                  </div>
                )}

                {/* Product Image */}
                <img
                  src={
                    product?.imageUrls?.length > 0
                      ? product.imageUrls[0]
                      : prod1
                  }
                  alt={product?.title}
                  className="w-full h-full object-cover object-center"
                />
              </div>

              <div className="flex flex-col justify-between flex-1 p-4">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    {product?.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {string.capitalizeFirst(product?.investmentType)}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-y-2 mb-3">
                  <p className="text-sm text-gray-700 font-medium col-span-2">
                    Opening deposit
                  </p>
                  <p className="text-sm text-gray-700 font-medium">ROI</p>

                  <p className="text-sm text-gray-700 col-span-2">
                    {formatter.format(
                      product?.minDeposit?.$numberDecimal,
                      product?.assetId?.asset?.toUpperCase()
                    )}{" "}
                    -{" "}
                    {formatter.format(
                      product?.maxDeposit?.$numberDecimal,
                      product?.assetId?.asset?.toUpperCase()
                    )}
                  </p>
                  <p className="text-sm text-gray-700">
                    {product?.roi?.$numberDecimal} %
                  </p>
                </div>

                <button
                  className="py-3 px-10 text-sm font-bold text-white rounded-full bg-gradient-to-tr from-purple-500 to-rose-500 hover:opacity-90"
                  onClick={() => onView?.(product?._id)}
                >
                  View Details
                </button>
              </div>
            </div>
          ))
        ) : (
          // Show when no products found
          <div className="text-start text-gray-500 py-10">
            No products found
          </div>
        )}
      </div>
      <div className="flex items-center justify-center gap-4 mt-6">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
        >
          Prev
        </button>

        <span className="text-sm font-medium">
          Page {currentPage} of {totalPages}
        </span>

        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </section>
  );
}
